<template>
  <div class="expiration-form-wrapper">
    <modal ref='confirmationModal' data-backdrop="false">
      <template #body>
        <div class="modal-body">
          {{ $t('expiration.email_message') }}
          <strong>{{ $t('expiration.immediately') }}</strong>
          {{ $t('expiration.to_to') }}
          {{ $t('expiration.request') }}
          <strong>{{ $t('expiration.no_no') }}</strong>
          {{ $t('expiration.and_and') }}
          <strong>{{ $t('expiration.update_again') }}</strong>
          {{ $t('expiration.update_changes') }}
        </div>

        <div class="modal-footer">
          <button @click.prevent='$refs.confirmationModal.close' class='btn btn-outline-primary'>{{ $t('expiration.cancel') }}</button>
          <button @click.prevent='confirm' class='btn btn-primary' :disabled="isProcessing">{{ $t('expiration.ok') }}</button>
        </div>
      </template>
    </modal>

    <form @submit.prevent='confirmOrSubmit'>
      <div class="form-group">
        <label>{{ $t('expiration.title') }}</label>
        <div class="input-group">
          <datepicker
                  name="record[expiration_date]"
                  v-model="record.expiration_date"
                  input-class="form-control expiration-input"
                  :typeable="true"
                  :full-month-name="true"
                  :monday-first="true"
                  :placeholder="$t('expiration.pick_date')">
          </datepicker>

          <div class="input-group-append">
            <span id="basic-addon1" class="input-group-text">
              <i class="fas fa-calendar"></i>
            </span>
          </div>
        </div>
      </div>

      <div v-if='contact' class="form-group">
        <label v-if='contact.name'>
          {{ $t('expiration.send_reminders') }} <strong>{{ contact.name }}</strong>
        </label>
        <label v-else>
          {{ $t('expiration.send_reminders_to_contacts') }}
        </label>
        <select name="record[contact_reminder_days]" class="form-control expiration-select" v-model="contactReminderDays">
          <option value="-1" selected> {{ $t('expiration.no_reminder') }}</option>
          <option value="0"> {{ $t('expiration.yes_on_expiration_date') }}</option>
          <option value="5"> {{ $t('expiration.yes_5_days') }}</option>
          <option value="10"> {{ $t('expiration.yes_10_days') }}</option>
          <option value="15"> {{ $t('expiration.yes_2_weeks') }}</option>
          <option value="30"> {{ $t('expiration.yes_1_month') }}</option>
        </select>
      </div>

      <div class="form-group">
        <label>{{ $t('expiration.notify_me_before') }}</label>
        <select name="record[user_reminder_days]" class="form-control expiration-select" v-model='record.user_reminder_days'>
          <option value="" selected> {{ $t('expiration.no_reminder') }}</option>
          <option value="0"> {{ $t('expiration.yes_on_expiration_date') }}</option>
          <option value="5"> {{ $t('expiration.yes_5_days') }}</option>
          <option value="10"> {{ $t('expiration.yes_10_days') }}</option>
          <option value="15"> {{ $t('expiration.yes_2_weeks') }}</option>
          <option value="30"> {{ $t('expiration.yes_1_month') }}</option>
        </select>
      </div>

      <div class="form-group">
        <label>{{ $t('expiration.reapeat_cycle') }}</label>
        <select name="record[revalidation_interval]" class="form-control expiration-select" v-model='record.revalidation_interval'>
          <option value="0" selected> {{ $t('expiration.no_reminder') }}</option>
          <option value="6"> {{ $t('expiration.after_6_months') }}</option>
          <option value="12"> {{ $t('expiration.after_1_year') }}</option>
          <option value="24"> {{ $t('expiration.after_2_year') }}</option>
          <option value="36"> {{ $t('expiration.after_3_year') }}</option>
          <option value="48"> {{ $t('expiration.after_4_year') }}</option>
        </select>

        <span>
          {{ $t('expiration.info_cycle') }}
        </span>
      </div>
      <div class="form-group">
        <label>
          {{ $t('request_new_version.skip_auto_replace') }}
          <input type="hidden" name="record[skip_automatic_version_change]" :value='record.skip_automatic_version_change'>
          <input type='checkbox' :value='record.skip_automatic_version_change' v-model='record.skip_automatic_version_change' name="record[skip_automatic_version_change]"/>
        </label>
      </div>
      <div class="form-group float-right">
        <input type="submit" class="btn btn-primary" :disabled="isProcessing" :value= "$t('expiration.update')">
      </div>
    </form>
  </div>
</template>

<script>
  import Datepicker from 'vuejs-datepicker'
  import _modal from '../_modal'

  export default {
    components: {
      modal: _modal,
      datepicker: Datepicker
    },
    props: {
      record: {
        type: Object,
        default: () => { return {} }
      },
      contact: {
        type: Object
      },
      submitCallback: {
        type: Function,
        required: true
      },
      orgContactReminderDays: {
        default: null
      }
    },
    data() {
      return {
        lastFormSubmitEvent: null,
        isProcessing: false,
        contactReminderDays: String(this.$props.record.contact_reminder_days) === '0' ? String(this.$props.record.contact_reminder_days)  : this.$props.record.contact_reminder_days || (this.$props.orgContactReminderDays === '0' ? '0' : parseInt(this.$props.orgContactReminderDays) || -1 )
      }
    },
    methods: {
      confirm() {
        this.isProcessing = true
        this.submitCallback(this.lastFormSubmitEvent).then(() => {
          this.$refs.confirmationModal.close()
          this.isProcessing = false
        }).catch(() => {
          this.isProcessing = false
        })
      },
      confirmOrSubmit(event) {
        const today = new Date()
        const expirationDate = new Date(this.$props.record.expiration_date)

        let contactReminderBeforeToday = parseInt(this.contactReminderDays) >= 0 ? parseInt(this.contactReminderDays) : NaN
        if(!isNaN(contactReminderBeforeToday))
          contactReminderBeforeToday = expirationDate.setDate(expirationDate.getDate() - contactReminderBeforeToday) <= today

        // setDate() changes value of const expirationDate
        const expirationDate1 = new Date(this.$props.record.expiration_date)

        let userReminderBeforeToday = parseInt(this.$props.record.user_reminder_days)
        if (!isNaN(userReminderBeforeToday))
          userReminderBeforeToday = expirationDate1.setDate(expirationDate1.getDate() - userReminderBeforeToday) <= today

        if (contactReminderBeforeToday || userReminderBeforeToday) {
          this.lastFormSubmitEvent = event
          this.$refs.confirmationModal.open()
        } else {
          this.$props.submitCallback(event)
        }
      }
    }
  }
</script>
<style lang="sass" scoped>
  .vdp-datepicker
    width: 87%
  .expiration-form-wrapper
    padding: 0 20px
    .modal
      background: rgba(0, 0, 0, 0.5)
</style>
